import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, Phone, MapPin } from "lucide-react";
import { SERVICE_PLANS } from "./Services";
import { ADDONS } from "./AddOns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialState = {
    name: "",
    email: "",
    phone: "",
    vehicle_type: "",
    package: "",
    preferred_date: "",
    message: "",
};

// Parse the first integer found in a price string (e.g. "$75–$95" -> 75, "+$25" -> 25)
const parsePrice = (str) => {
    if (!str) return 0;
    const match = String(str).match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
};

export default function ContactForm({
    selectedAddons,
    selectedPackage,
    onPackageChange,
    onToggleAddon,
}) {
    const [form, setForm] = useState(initialState);
    const [submitting, setSubmitting] = useState(false);

    const selectedPlan = SERVICE_PLANS.find((p) => p.name === selectedPackage);
    const packageBase = selectedPlan ? parsePrice(selectedPlan.price) : 0;
    const addonsTotal = (selectedAddons || []).reduce((sum, name) => {
        const a = ADDONS.find((x) => x.name === name);
        return sum + (a ? parsePrice(a.price) : 0);
    }, 0);
    const estimatedTotal = packageBase + addonsTotal;

    const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.message.trim()) {
            toast.error("Please fill in name, email, phone, and message.");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                ...form,
                package: selectedPackage || form.package || null,
                addons: selectedAddons || [],
            };
            await axios.post(`${API}/contact`, payload);
            toast.success("Message sent. We'll reach out shortly!");
            setForm(initialState);
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Something went wrong. Please try again or call us.";
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 md:py-32 px-6 md:px-12 bg-[#0A0A0A] border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <div className="font-heading text-xs uppercase tracking-[0.32em] text-[#9D4CDD] font-bold mb-4">
                        / Contact & Scheduling
                    </div>
                    <h2 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-white">
                        Let&apos;s make it{" "}
                        <span className="text-[#9D4CDD]">shine.</span>
                    </h2>
                    <p className="mt-5 text-white/60 leading-relaxed">
                        Send us your vehicle info and preferred date. We&apos;ll
                        confirm your slot within 24 hours.
                    </p>

                    <div className="mt-10 space-y-5">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 flex items-center justify-center border border-[#9D4CDD]/30 bg-[#9D4CDD]/10">
                                <MapPin className="text-[#9D4CDD]" size={16} />
                            </div>
                            <div>
                                <div className="font-heading text-xs uppercase tracking-[0.24em] text-white/40">
                                    Service Area
                                </div>
                                <div className="font-heading text-white font-semibold">
                                    Surprise, AZ &amp; West Valley
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 flex items-center justify-center border border-[#9D4CDD]/30 bg-[#9D4CDD]/10">
                                <Phone className="text-[#9D4CDD]" size={16} />
                            </div>
                            <div>
                                <div className="font-heading text-xs uppercase tracking-[0.24em] text-white/40">
                                    Call or Text
                                </div>
                                <div className="font-heading text-white font-semibold">
                                    (702) 376-6498
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 flex items-center justify-center border border-[#9D4CDD]/30 bg-[#9D4CDD]/10">
                                <Mail className="text-[#9D4CDD]" size={16} />
                            </div>
                            <div>
                                <div className="font-heading text-xs uppercase tracking-[0.24em] text-white/40">
                                    Email
                                </div>
                                <div className="font-heading text-white font-semibold">
                                    aiden.chapa@acdetailingaz.com
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <form
                    data-testid="contact-form"
                    onSubmit={handleSubmit}
                    className="lg:col-span-7 bg-[#121212] border border-white/5 p-8 md:p-10 space-y-6"
                    noValidate
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FieldWrap label="Full Name" required>
                            <input
                                data-testid="contact-input-name"
                                type="text"
                                value={form.name}
                                onChange={update("name")}
                                placeholder="Jordan Smith"
                                className={inputCls}
                                required
                            />
                        </FieldWrap>
                        <FieldWrap label="Email" required>
                            <input
                                data-testid="contact-input-email"
                                type="email"
                                value={form.email}
                                onChange={update("email")}
                                placeholder="you@email.com"
                                className={inputCls}
                                required
                            />
                        </FieldWrap>
                        <FieldWrap label="Phone" required>
                            <input
                                data-testid="contact-input-phone"
                                type="tel"
                                value={form.phone}
                                onChange={update("phone")}
                                placeholder="(623) 555-0142"
                                className={inputCls}
                                required
                            />
                        </FieldWrap>
                        <FieldWrap label="Vehicle (Year / Make / Model)">
                            <input
                                data-testid="contact-input-vehicle"
                                type="text"
                                value={form.vehicle_type}
                                onChange={update("vehicle_type")}
                                placeholder="2021 Toyota Tacoma"
                                className={inputCls}
                            />
                        </FieldWrap>
                        <FieldWrap label="Base Package">
                            <select
                                data-testid="contact-select-package"
                                value={selectedPackage || ""}
                                onChange={(e) => onPackageChange?.(e.target.value)}
                                className={`${inputCls} appearance-none`}
                            >
                                <option value="">Select a package</option>
                                {SERVICE_PLANS.map((p) => (
                                    <option key={p.id} value={p.name}>
                                        {p.name} — {p.price}
                                    </option>
                                ))}
                            </select>
                        </FieldWrap>
                        <FieldWrap label="Preferred Date">
                            <input
                                data-testid="contact-input-date"
                                type="date"
                                value={form.preferred_date}
                                onChange={update("preferred_date")}
                                className={`${inputCls} [color-scheme:dark]`}
                            />
                        </FieldWrap>
                    </div>

                    <div>
                        <div className="font-heading text-[11px] uppercase tracking-[0.24em] text-white/60 font-bold mb-3">
                            Add-Ons ({selectedAddons.length})
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {ADDONS.map((a) => {
                                const active = selectedAddons.includes(a.name);
                                return (
                                    <button
                                        key={a.id}
                                        type="button"
                                        data-testid={`contact-addon-chip-${a.id}`}
                                        onClick={() => onToggleAddon(a.name)}
                                        className={`px-3 py-2 text-xs font-heading font-semibold uppercase tracking-[0.14em] border transition-all ${
                                            active
                                                ? "bg-[#9D4CDD] text-white border-[#9D4CDD]"
                                                : "bg-transparent text-white/70 border-white/15 hover:border-[#9D4CDD]/50"
                                        }`}
                                    >
                                        {a.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <FieldWrap label="Message / Notes" required>
                        <textarea
                            data-testid="contact-input-message"
                            value={form.message}
                            onChange={update("message")}
                            rows={5}
                            placeholder="Tell us about your vehicle, condition, and what you're hoping for..."
                            className={`${inputCls} resize-none`}
                            required
                        />
                    </FieldWrap>

                    <div className="flex flex-col md:flex-row gap-4 md:items-stretch">
                        <button
                            type="submit"
                            data-testid="contact-submit-btn"
                            disabled={submitting}
                            className="group inline-flex items-center justify-center gap-3 bg-[#9D4CDD] text-white px-8 py-4 font-heading text-[12px] font-bold tracking-[0.24em] uppercase hover:bg-[#B366F9] transition-all hover:shadow-[0_0_24px_rgba(157,76,221,0.45)] disabled:opacity-60 disabled:cursor-not-allowed md:flex-1"
                        >
                            {submitting ? "Sending..." : "Send Message"}
                            <Send
                                size={14}
                                className="transition-transform group-hover:translate-x-1"
                            />
                        </button>

                        <div
                            data-testid="estimated-total-box"
                            className="flex items-center justify-between gap-6 bg-[#1A1A1A] border border-[#9D4CDD]/30 px-5 py-3 md:min-w-[240px]"
                        >
                            <div>
                                <div className="font-heading text-[10px] uppercase tracking-[0.24em] text-white/50 font-bold">
                                    Estimated Total
                                </div>
                                <div className="text-[10px] text-white/40 mt-0.5">
                                    {selectedPlan ? selectedPlan.name : "No package"}
                                    {selectedAddons.length > 0 &&
                                        ` + ${selectedAddons.length} add-on${selectedAddons.length > 1 ? "s" : ""}`}
                                </div>
                            </div>
                            <div
                                data-testid="estimated-total-amount"
                                className="font-heading text-3xl font-black text-[#9D4CDD] tracking-tighter"
                            >
                                ${estimatedTotal}
                            </div>
                        </div>
                    </div>

                    <p
                        data-testid="contact-pricing-disclaimer"
                        className="text-xs text-white/45 leading-relaxed"
                    >
                        * Prices are estimates and may vary based on vehicle size
                        and condition. Final pricing will be agreed upon after a
                        vehicle inspection.
                    </p>
                </form>
            </div>
        </section>
    );
}

const inputCls =
    "w-full bg-[#1A1A1A] border border-white/10 text-white placeholder-white/30 px-4 py-3 text-sm font-sans focus:outline-none focus:border-[#9D4CDD] focus:ring-2 focus:ring-[#9D4CDD]/30 transition-all";

function FieldWrap({ label, required, children }) {
    return (
        <label className="block">
            <div className="font-heading text-[10px] uppercase tracking-[0.24em] text-white/60 font-bold mb-2">
                {label}
                {required && <span className="text-[#9D4CDD]"> *</span>}
            </div>
            {children}
        </label>
    );
}
